/**
 * The words of a teacher's guided experiment, after it has been recorded on
 * the bench: what each vessel is called, each instruction, what to look for,
 * what to say when a student does something else, pauses to watch, and the
 * conclusion. The ACTS are the recording's — changing what is done means
 * recording again.
 *
 * Drawn inside the bench, like the save dialog, because the bench may be full
 * screen. See docs/FeatureDesigns/lab-setups/lab-setups-target.md §6.4.
 */
import { useState } from 'react';
import { labSetups } from '@padcanvas/shared';
import type { GuidedExpectation } from '@padcanvas/shared';
import { VESSEL_LABELS } from '../vesselTypes';
import { insertWatch, renameSteps, vesselNames, type ChemistrySetupPayload, type ChemistrySetupVessel, type RecordedStep } from '../setupState';
import '../../lab-setups/labSetups.css';

export interface StepsEditorProps {
  vessels: ChemistrySetupVessel[];
  steps: RecordedStep[];
  conclusion?: string;
  initialName: string;
  initialNotes?: string;
  chapters?: readonly { id: string; label: string }[];
  initialChapterIds?: readonly string[];
  /** The saved setup this would save over, when one is open. */
  saveOverName?: string;
  busy?: boolean;
  error?: string | null;
  onSave: (payload: ChemistrySetupPayload, name: string, notes: string, chapterIds: string[], asNew: boolean) => void;
  /** Back to the bench, recording again from the starting bench. */
  onRecordAgain?: () => void;
  onCancel: () => void;
}

const KIND_LABEL: Record<GuidedExpectation['kind'], string> = {
  'add-substance': 'Pour',
  'dip-test': 'pH paper',
  'dip-rod': 'Glass rod',
  'attach-burner': 'Burner',
  heat: 'Heat',
  'connect-pipe': 'Tube',
  swirl: 'Swirl',
  observe: 'Watch',
  'answer-quantity': 'Number',
};

export function StepsEditor({
  vessels, steps: initialSteps, conclusion: initialConclusion = '', initialName, initialNotes = '', chapters = [],
  initialChapterIds = [], saveOverName, busy = false, error, onSave, onRecordAgain, onCancel,
}: StepsEditorProps): React.JSX.Element {
  const [name, setName] = useState(initialName);
  const [notes, setNotes] = useState(initialNotes);
  const [labels, setLabels] = useState<string[]>(() => vessels.map(v => v.label ?? ''));
  const [steps, setSteps] = useState<RecordedStep[]>(initialSteps);
  const [conclusion, setConclusion] = useState(initialConclusion);
  const [ticked, setTicked] = useState<string[]>(() => chapters.map(c => c.id).filter(id => initialChapterIds.includes(id)));

  const named = vessels.map((v, i) => ({ ...v, label: labels[i]?.trim() || undefined }));
  const names = vesselNames(named);
  const payload: ChemistrySetupPayload = {
    vessels: named.map(({ label, ...v }) => (label ? { ...v, label } : v)),
    steps: steps.map(r => r.step),
    ...(conclusion.trim() ? { conclusion: conclusion.trim() } : {}),
  };
  const reactions = labSetups.chemistryReactionCount(payload);
  const over = reactions > labSetups.CHEMISTRY_SESSION_REACTIONS;
  const blank = steps.some(r => !r.step.instruction.trim());
  /* A heat step whose vessel has no flame by then can never be done: lighting
     one is refused while the run is waiting for the heat. Said, not refused —
     the teacher may be about to move the burner step back up. */
  const unheatable = steps.flatMap((r, i) => {
    if (r.step.expect.kind !== 'heat') return [];
    const lit = vessels[r.step.container]?.burnerType !== 'none'
      || steps.slice(0, i).some(p => p.step.expect.kind === 'attach-burner' && p.step.container === r.step.container);
    return lit ? [] : [i + 1];
  });

  const rename = (i: number, label: string): void => {
    const next = labels.map((l, j) => (j === i ? label : l));
    setLabels(next);
    setSteps(s => renameSteps(s, vesselNames(vessels.map((v, j) => ({ ...v, label: next[j]?.trim() || undefined })))));
  };
  const patch = (i: number, change: Partial<RecordedStep['step']>, edited = false): void =>
    setSteps(s => s.map((r, j) => (j === i ? { ...r, edited: r.edited || edited, step: { ...r.step, ...change } } : r)));
  const move = (i: number, by: -1 | 1): void => setSteps(s => {
    const next = [...s];
    [next[i], next[i + by]] = [next[i + by], next[i]];
    return next;
  });

  const submit = (asNew: boolean): void => {
    if (!name.trim() || busy || over || blank || steps.length === 0) return;
    onSave(payload, name.trim(), notes.trim(), chapters.map(c => c.id).filter(id => ticked.includes(id)), asNew);
  };

  return (
    <div className="labsetup-overlay" onMouseDown={e => { if (e.target === e.currentTarget && !busy) onCancel(); }}>
      <form className="labsetup-dialog chemlab-steps-editor" role="dialog" aria-modal="true" aria-label="Guided experiment"
        onSubmit={e => { e.preventDefault(); submit(!saveOverName); }}
        onKeyDown={e => { if (e.key === 'Escape' && !busy) { e.stopPropagation(); onCancel(); } }}>
        <h3>Guided experiment</h3>
        <label>
          <span>Name</span>
          <input value={name} maxLength={labSetups.LAB_SETUP_NAME_MAX} onChange={e => setName(e.target.value)}
            placeholder="Carbon dioxide and lime water, for 10A" autoFocus />
        </label>

        <fieldset className="chemlab-steps-vessels">
          <legend>The vessels <em>(what the steps call them)</em></legend>
          {vessels.map((v, i) => (
            <label key={i}>
              <span>{VESSEL_LABELS[v.vesselType]}{vessels.length > 1 ? ` ${i + 1}` : ''}</span>
              <input value={labels[i]} maxLength={40} placeholder={names[i]} onChange={e => rename(i, e.target.value)} />
            </label>
          ))}
        </fieldset>

        <ol className="chemlab-steps-list">
          {steps.map((r, i) => (
            <li key={i}>
              <div className="chemlab-steps-row">
                <span className="chemlab-steps-kind">{i + 1} · {KIND_LABEL[r.step.expect.kind]}</span>
                <span className="chemlab-steps-tools">
                  <button type="button" aria-label="Move up" title="Move up" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                  <button type="button" aria-label="Move down" title="Move down" disabled={i === steps.length - 1} onClick={() => move(i, 1)}>↓</button>
                  <button type="button" title="Add a pause to watch, after this step" onClick={() => setSteps(s => insertWatch(s, i, names))}>+ Watch</button>
                  <button type="button" aria-label="Delete step" title="Delete step" onClick={() => setSteps(s => s.filter((_, j) => j !== i))}>✕</button>
                </span>
              </div>
              <textarea rows={2} value={r.step.instruction} maxLength={300} aria-label={`Step ${i + 1} instruction`}
                onChange={e => patch(i, { instruction: e.target.value }, true)} />
              <input value={r.step.observe ?? ''} maxLength={300} placeholder="What to look for (optional)"
                aria-label={`Step ${i + 1} — what to look for`}
                onChange={e => patch(i, { observe: e.target.value || undefined })} />
              {r.step.expect.kind !== 'observe' && (
                <input value={r.step.correction ?? ''} maxLength={300}
                  placeholder="If a student does something else, say… (optional)"
                  aria-label={`Step ${i + 1} — what to say to a student who does something else`}
                  onChange={e => patch(i, { correction: e.target.value || undefined })} />
              )}
            </li>
          ))}
        </ol>
        {steps.length === 0 && <p className="labsetup-hint">No steps left. Record again, or cancel to keep what was saved.</p>}

        <label>
          <span>What it shows <em>(shown at the end)</em></span>
          <textarea rows={2} value={conclusion} maxLength={1000} onChange={e => setConclusion(e.target.value)}
            placeholder="Carbon dioxide turns lime water milky." />
        </label>
        <label>
          <span>Notes <em>(optional, for you)</em></span>
          <textarea value={notes} maxLength={labSetups.LAB_SETUP_NOTES_MAX} rows={2} onChange={e => setNotes(e.target.value)} />
        </label>

        {chapters.length > 1 && (
          <fieldset className="labsetup-chapters">
            <legend>Show under <em>(on the Chapters tab)</em></legend>
            {chapters.map(c => (
              <label key={c.id}>
                <input type="checkbox" checked={ticked.includes(c.id)}
                  onChange={() => setTicked(t => (t.includes(c.id) ? t.filter(x => x !== c.id) : [...t, c.id]))} />
                <span>{c.label}</span>
              </label>
            ))}
          </fieldset>
        )}

        <p className={`labsetup-hint ${over ? 'chemlab-steps-over' : ''}`}>
          {reactions} of {labSetups.CHEMISTRY_SESSION_REACTIONS} reactions a run allows
          {over ? ' — take out some steps, or split it in two.' : '.'}
        </p>
        {unheatable.length > 0 && (
          <p className="labsetup-hint chemlab-steps-over">
            Step {unheatable.join(', ')} heats a vessel with no burner under it yet — keep the burner step before it.
          </p>
        )}
        {error && <p className="labsetup-error" role="alert">{error}</p>}
        <div className="labsetup-actions">
          {onRecordAgain && <button type="button" onClick={onRecordAgain} disabled={busy}>Record again</button>}
          <span className="chemlab-steps-gap" />
          <button type="button" onClick={onCancel} disabled={busy}>Cancel</button>
          {saveOverName && (
            <button type="button" onClick={() => submit(true)} disabled={busy || !name.trim() || over || blank || steps.length === 0}>Save as new</button>
          )}
          <button type="submit" className="labsetup-primary" disabled={busy || !name.trim() || over || blank || steps.length === 0}>
            {busy ? 'Saving…' : saveOverName ? 'Save' : 'Save experiment'}
          </button>
        </div>
      </form>
    </div>
  );
}
